import { ZuiTableBaseElement } from './base.js';
import { html } from 'lit';
import { queryAssignedElements, queryAssignedNodes } from 'lit/decorators.js';
import { style } from './zui-table-topbar-css.js';
import { screenBreakpoints } from '@zywave/zui-base/dist/utils/breakpoints.js';
import type { ZuiButton } from '@zywave/zui-button/dist/zui-button.js';

/**
 * Used to house table related pieces such as a search input or `<zui-search>`, and other up front information.
 *
 * @element zui-table-topbar
 *
 * @slot - Default, unnamed slot; for inserting content into `<zui-table-topbar>`
 * @slot counter - Total number of results from table goes here
 * @slot action - Action(s) that affect the whole table goes here
 */
export class ZuiTableTopbarElement extends ZuiTableBaseElement {
  @queryAssignedElements({ slot: 'action' })
  private _actionSlottedElements: Array<HTMLCollection>;

  @queryAssignedNodes({ slot: 'counter' })
  private _counterSlottedNodes: Array<HTMLCollection>;

  @queryAssignedElements({ selector: 'zui-button', slot: 'action' })
  private _actionButtons: Array<ZuiButton>;

  #mobileBreakpoint: MediaQueryList = window.matchMedia(`only screen and (max-width: ${screenBreakpoints.xsmall})`);

  #mobileTopbarActionButtonsEvent = (b: MediaQueryListEvent) => {
    b.matches ? this.#addBlockStylingToButtons() : this.#removeBlockStylingFromButtons();
  };

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    this.#mobileBreakpoint.addEventListener('change', this.#mobileTopbarActionButtonsEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mobileBreakpoint.removeEventListener('change', this.#mobileTopbarActionButtonsEvent);
  }

  firstUpdated() {
    this.#mobileBreakpoint.matches ? this.#addBlockStylingToButtons() : this.#removeBlockStylingFromButtons();
    this.#toggleActionDivDisplay();
    this.#toggleCounterDivDisplay();
  }

  render() {
    return html`<div class="topbar">
      <div class="content"><slot></slot></div>
      <div class="counter"><slot name="counter" @slotchange="${this.#toggleCounterDivDisplay}"></slot></div>
      <div class="action"><slot name="action" @slotchange=${this.#handleActionSlotchange}></slot></div>
    </div>`;
  }

  #handleActionSlotchange() {
    this.#toggleActionDivDisplay();
    this.#mobileBreakpoint.matches ? this.#addBlockStylingToButtons() : this.#removeBlockStylingFromButtons();
  }

  #addBlockStylingToButtons() {
    if (this._actionButtons.length > 0) {
      for (const button of this._actionButtons) {
        if (!button.hasAttribute('block')) {
          button.setAttribute('block', '');
        }
      }
    }

    this.requestUpdate();
  }

  #removeBlockStylingFromButtons() {
    if (this._actionButtons.length > 0) {
      for (const button of this._actionButtons) {
        if (button.hasAttribute('block')) {
          button.removeAttribute('block');
        }
      }
    }

    this.requestUpdate();
  }

  #toggleCounterDivDisplay() {
    const counter: HTMLElement = this.shadowRoot.querySelector('.counter');

    if (this._counterSlottedNodes.length === 0) {
      counter.style.display = 'none';
    } else if (this._counterSlottedNodes.length > 0 && counter.style.display === 'none') {
      counter.style.removeProperty('display');
    }

    this.requestUpdate();
  }

  #toggleActionDivDisplay() {
    const action: HTMLElement = this.shadowRoot.querySelector('.action');

    if (this._actionSlottedElements.length === 0) {
      action.style.display = 'none';
    } else if (this._actionSlottedElements.length > 0 && action.style.display === 'none') {
      action.style.removeProperty('display');
    }

    this.requestUpdate();
  }
}

window.customElements.define('zui-table-topbar', ZuiTableTopbarElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-table-topbar': ZuiTableTopbarElement;
  }
}
